import { useEffect, type ReactNode } from 'react'

import type { Rarity } from '../lib/api'

/**
 * A centred dialog over a blurred backdrop. Same fixed-overlay convention as
 * `RipTheatre`'s reveal stage, so a modal on this site always feels like the
 * same piece of chrome rather than a one-off per page.
 */
export function Modal({
  title,
  onClose,
  children,
}: {
  title: string
  onClose: () => void
  children: ReactNode
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 grid place-items-center overflow-y-auto overscroll-contain p-4"
      style={{ background: 'rgb(0 0 0 / 0.75)', backdropFilter: 'blur(6px)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="panel w-full max-w-md p-5">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold">{title}</h2>
          <button
            className="text-faint -m-1.5 rounded p-1.5 text-lg leading-none hover:opacity-70"
            style={{ color: 'var(--color-muted)' }}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  )
}

export const RARITY_LABEL: Record<Rarity, string> = {
  common: 'Common',
  uncommon: 'Uncommon',
  rare: 'Rare',
  epic: 'Epic',
  legendary: 'Legendary',
  mythic: 'Mythic',
}

export const rarityClass = (rarity: Rarity) => `rarity-${rarity}`

export function RarityChip({ rarity }: { rarity: Rarity }) {
  return <span className={`chip ${rarityClass(rarity)}`}>{RARITY_LABEL[rarity]}</span>
}

/**
 * Tier colour and label both come from `@exlyr/shared` — the same table the
 * payout engine and the printed wrapper are generated from. Re-exported here so
 * components keep importing their vocabulary from one place, and so a tier can
 * never be renamed in the UI but not in the ledger.
 */
export { TIER_ACCENT, TIER_LABEL } from '@exlyr/shared'

export function Stat({
  label,
  value,
  sub,
  tone,
}: {
  label: string
  value: ReactNode
  sub?: ReactNode
  tone?: 'up' | 'down'
}) {
  const color = tone === 'up' ? 'var(--color-up)' : tone === 'down' ? 'var(--color-down)' : undefined
  return (
    <div>
      <div className="eyebrow">{label}</div>
      <div className="tnum mt-1 text-lg font-semibold" style={{ color }}>
        {value}
      </div>
      {sub !== undefined && <div className="text-faint mt-0.5 text-xs">{sub}</div>}
    </div>
  )
}

export function Skeleton({ className = '' }: { className?: string }) {
  return (
    <div
      className={`animate-pulse rounded-lg ${className}`}
      style={{ background: 'var(--color-surface-2)' }}
      aria-hidden
    />
  )
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string
  body: string
  action?: ReactNode
}) {
  return (
    <div className="panel grid place-items-center px-6 py-16 text-center">
      <div className="max-w-sm">
        <div className="text-base font-semibold">{title}</div>
        <p className="mt-2 text-sm" style={{ color: 'var(--color-muted)' }}>
          {body}
        </p>
        {action && <div className="mt-5">{action}</div>}
      </div>
    </div>
  )
}

export function SectionHeading({
  title,
  sub,
  action,
}: {
  title: string
  sub?: string
  action?: ReactNode
}) {
  return (
    <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
        {sub && (
          <p className="mt-1 text-sm" style={{ color: 'var(--color-muted)' }}>
            {sub}
          </p>
        )}
      </div>
      {action}
    </div>
  )
}

/**
 * Chain badge. Almost everything drawable lives on Robinhood Chain (`RHC`);
 * `crypto` is the deliberate exception — BTC, SOL and the tokenized metals,
 * priced off the general market because none of them exists on the chain in
 * any form — and gets its own colour precisely so it never reads as a
 * Robinhood Chain token. `stock` is
 * only reachable from a historical position held from before the equity half
 * was retired, and it is labelled so those do not silently read as tokens.
 */
const KIND_BADGE: Record<'stock' | 'coin' | 'crypto', { bg: string; fg: string; label: string }> = {
  coin: { bg: 'var(--color-brand-wash)', fg: 'var(--color-brand)', label: 'RHC' },
  crypto: { bg: 'color-mix(in srgb, #22d3ee 16%, transparent)', fg: '#22d3ee', label: 'Market' },
  stock: { bg: 'var(--color-surface-3)', fg: 'var(--color-muted)', label: 'Retired' },
}

export function KindBadge({ kind }: { kind: 'stock' | 'coin' | 'crypto' }) {
  const { bg, fg, label } = KIND_BADGE[kind]
  return (
    <span
      className="rounded-none px-1.5 py-0.5 text-[0.625rem] font-semibold tracking-wider uppercase"
      style={{ background: bg, color: fg }}
    >
      {label}
    </span>
  )
}

export function Delta({ value, suffix = '%' }: { value: number; suffix?: string }) {
  const up = value >= 0
  return (
    <span
      className="tnum text-xs font-medium"
      style={{ color: up ? 'var(--color-up)' : 'var(--color-down)' }}
    >
      {up ? '+' : '−'}
      {Math.abs(value).toFixed(2)}
      {suffix}
    </span>
  )
}
